import * as React from "react";
import { Check, ChevronDown, Loader2, Search, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export interface StudentOption {
  value: string;
  label: string;
  /** Secondary line under the name, e.g. the university or status. */
  description?: string;
  /** Rows with the same group are listed together under a collapsible header. */
  group?: string;
}

interface StudentPickerProps {
  options: StudentOption[];
  value: string | null | undefined;
  onChange: (value: string | null) => void;
  placeholder?: string;
  title?: string;
  subtitle?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  /** Label for the group filter's "everything" option, e.g. "All universities". */
  allGroupsLabel?: string;
  disabled?: boolean;
  loading?: boolean;
  className?: string;
  id?: string;
  "aria-invalid"?: boolean;
  "aria-describedby"?: string;
}

const AVATAR_TONES = [
  "bg-pastel-lavender text-pastel-lavender-ink",
  "bg-pastel-rose text-pastel-rose-ink",
  "bg-pastel-mint text-pastel-mint-ink",
  "bg-pastel-sky text-pastel-sky-ink",
  "bg-pastel-lilac text-pastel-lilac-ink",
  "bg-pastel-peach text-pastel-peach-ink",
];

function initialsOf(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : "")).toUpperCase();
}

function toneOf(key: string) {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return AVATAR_TONES[h % AVATAR_TONES.length];
}

export function StudentAvatar({ name, className }: { name: string; className?: string }) {
  return (
    <span
      aria-hidden
      className={cn(
        "flex size-10 shrink-0 items-center justify-center rounded-full text-[13px] font-medium",
        toneOf(name),
        className,
      )}
    >
      {initialsOf(name)}
    </span>
  );
}

/**
 * Student chooser in the "Select Student" panel style: a search pill, optional
 * group filter, collapsible groups, pastel initials and a black Select button.
 * The choice is staged in the panel and only committed when Select is pressed.
 */
export function StudentPicker({
  options,
  value,
  onChange,
  placeholder = "Choose a student",
  title = "Select student",
  subtitle = "Search by name, then confirm with Select.",
  searchPlaceholder = "Search for a student",
  emptyText = "No students available",
  allGroupsLabel = "All groups",
  disabled,
  loading,
  className,
  id,
  ...aria
}: StudentPickerProps) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [group, setGroup] = React.useState<string>("all");
  const [pending, setPending] = React.useState<string | null>(value ?? null);
  const [collapsed, setCollapsed] = React.useState<Record<string, boolean>>({});
  const selected = options.find((o) => o.value === value);

  React.useEffect(() => {
    if (open) {
      setPending(value ?? null);
      setQuery("");
    }
  }, [open, value]);

  const groups = React.useMemo(() => {
    const names = Array.from(new Set(options.map((o) => o.group).filter(Boolean))) as string[];
    return names.sort((a, b) => a.localeCompare(b));
  }, [options]);

  const sections = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    const matches = options.filter(
      (o) =>
        (group === "all" || o.group === group) &&
        (!q || o.label.toLowerCase().includes(q) || (o.description ?? "").toLowerCase().includes(q)),
    );
    const map = new Map<string, StudentOption[]>();
    for (const o of matches) {
      const key = o.group || "";
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(o);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [options, query, group]);

  const commit = () => {
    onChange(pending);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          id={id}
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-haspopup="dialog"
          disabled={disabled}
          {...aria}
          className={cn(
            "flex h-12 w-full min-w-0 items-center gap-3 rounded-full border border-transparent bg-input-background pr-4 pl-1.5 text-left text-sm transition-colors",
            "hover:border-input focus-visible:border-hairline-strong focus-visible:ring-4 focus-visible:ring-ring/15 focus-visible:outline-none",
            "disabled:cursor-not-allowed disabled:opacity-50 aria-[invalid=true]:border-destructive/70",
            !selected && "pl-4",
            className,
          )}
        >
          {selected && <StudentAvatar name={selected.label} className="size-9 text-xs" />}
          <span className="min-w-0 flex-1">
            {selected ? (
              <>
                <span className="block truncate font-medium text-foreground">{selected.label}</span>
                {selected.description && (
                  <span className="block truncate text-xs text-muted-foreground">{selected.description}</span>
                )}
              </>
            ) : (
              <span className="block truncate text-muted-foreground">{loading ? "Loading…" : placeholder}</span>
            )}
          </span>
          {loading ? (
            <Loader2 className="size-4 shrink-0 animate-spin text-muted-foreground" />
          ) : (
            <ChevronDown className="size-4 shrink-0 text-muted-foreground" />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={8}
        className="flex max-h-[min(640px,var(--radix-popover-content-available-height))] w-[min(440px,calc(100vw-2rem))] flex-col rounded-2xl p-0"
      >
        <div className="flex items-start justify-between gap-3 px-6 pt-6 pb-4">
          <div className="min-w-0">
            <p className="text-[17px] leading-6 font-medium text-foreground">{title}</p>
            <p className="mt-0.5 text-sm text-muted-foreground">{subtitle}</p>
          </div>
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label="Close"
            className="flex size-9 shrink-0 items-center justify-center rounded-full bg-muted text-body transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X className="size-4" />
          </button>
        </div>

        <div className="mx-6 border-t border-border" />

        <div className="space-y-3 px-6 pt-4 pb-2">
          {groups.length > 1 && (
            <div className="flex justify-end">
              <label className="relative">
                <span className="sr-only">Filter by group</span>
                <select
                  value={group}
                  onChange={(e) => setGroup(e.target.value)}
                  className="h-9 max-w-[220px] appearance-none truncate rounded-full border border-border bg-card pr-9 pl-4 text-[13px] text-foreground transition-colors hover:border-hairline-strong focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
                >
                  <option value="all">{allGroupsLabel}</option>
                  {groups.map((g) => (
                    <option key={g} value={g}>
                      {g}
                    </option>
                  ))}
                </select>
                <ChevronDown className="pointer-events-none absolute top-1/2 right-3.5 size-3.5 -translate-y-1/2 text-body" />
              </label>
            </div>
          )}
          <div className="relative">
            <Search className="pointer-events-none absolute top-1/2 left-4 size-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
              autoFocus
              className="h-11 w-full rounded-full border border-transparent bg-input-background pr-4 pl-11 text-sm text-foreground placeholder:text-muted-foreground/80 focus-visible:border-hairline-strong focus-visible:bg-card focus-visible:ring-4 focus-visible:ring-ring/15 focus-visible:outline-none [&::-webkit-search-cancel-button]:hidden"
            />
          </div>
        </div>

        <div role="listbox" aria-label={title} className="min-h-0 flex-1 overflow-y-auto px-4 pb-2">
          {sections.length === 0 && (
            <p className="px-2 py-10 text-center text-sm text-muted-foreground">
              {loading ? "Loading…" : emptyText}
            </p>
          )}
          {sections.map(([name, rows]) => {
            const isCollapsed = !!name && collapsed[name] && !query;
            return (
              <div key={name || "_"} className="border-b border-border py-2 last:border-b-0">
                {name && (
                  <button
                    type="button"
                    onClick={() => setCollapsed((c) => ({ ...c, [name]: !c[name] }))}
                    aria-expanded={!isCollapsed}
                    className="flex w-full items-center gap-3 rounded-xl px-2 py-2 text-left transition-colors hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-muted text-sm font-medium text-pastel-lavender-ink">
                      #
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[15px] font-medium text-foreground">{name}</span>
                      <span className="block text-xs text-muted-foreground">
                        {rows.length} {rows.length === 1 ? "student" : "students"}
                      </span>
                    </span>
                    <ChevronDown
                      className={cn("size-4 text-body transition-transform", !isCollapsed && "rotate-180")}
                      aria-hidden
                    />
                  </button>
                )}
                {!isCollapsed && (
                  <ul className={cn("space-y-0.5", name && "mt-1 pl-4")}>
                    {rows.map((o) => {
                      const active = o.value === pending;
                      return (
                        <li key={o.value}>
                          <button
                            type="button"
                            role="option"
                            aria-selected={active}
                            onClick={() => setPending(active ? null : o.value)}
                            onDoubleClick={() => {
                              onChange(o.value);
                              setOpen(false);
                            }}
                            className={cn(
                              "flex w-full items-center gap-3 rounded-2xl px-2 py-2 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                              active ? "bg-muted" : "hover:bg-muted/60",
                            )}
                          >
                            <StudentAvatar name={o.label} />
                            <span className="min-w-0 flex-1">
                              <span className="block truncate text-[15px] text-foreground">{o.label}</span>
                              {o.description && (
                                <span className="block truncate text-[13px] text-muted-foreground">
                                  {o.description}
                                </span>
                              )}
                            </span>
                            <span
                              aria-hidden
                              className={cn(
                                "flex size-6 shrink-0 items-center justify-center rounded-full transition-colors",
                                active ? "bg-primary text-primary-foreground" : "border border-border",
                              )}
                            >
                              {active && <Check className="size-3.5" strokeWidth={3} />}
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            );
          })}
        </div>

        <div className="px-6 pt-2 pb-6">
          <Button type="button" size="lg" className="w-full" onClick={commit} disabled={!pending && !value}>
            Select
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default StudentPicker;
